import { Collection, getFirebase } from 'firestorter';
import moment from 'moment';

import BaseDocument from '../../Model/BaseDocument';
import Collections from '../../utils/collections';

/**
 * Enum for message direction in a lead conversation
 * @enum {string}
 */
const MessageDirection = {
  INBOUND: 'INBOUND',
  OUTBOUND: 'OUTBOUND',
};

const CONVERSATIONS_PAGE_SIZE = 100;

const LEAD_CLAIM_VALIDITY_HOURS = 1;

/**
 * Represents a leadConversations document — a per-lead summary of the latest
 * messaging activity. Each document is keyed by leadId and tracks the most
 * recent message details, assignment/claim state, and archive status.
 * Inbound messages unarchive the conversation; outbound messages archive it.
 *
 * @class LeadConversation
 * @extends BaseDocument
 */
class LeadConversation extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.LEAD_CONVERSATION}/${id}`, opts);
  }

  /**
   * Get the lead ID associated with this conversation
   * @returns {string} The lead ID
   */
  get leadId() {
    return this.data.leadId || '';
  }

  /**
   * Get the name of the lead in this conversation
   * @returns {string} The lead name
   */
  get leadName() {
    return this.data.leadName || '';
  }

  /**
   * Get the phone number of the lead in this conversation
   * @returns {string} The lead phone number
   */
  get leadPhoneNumber() {
    return this.data.leadPhoneNumber || '';
  }

  /**
   * Get the email of the lead in this conversation
   * @returns {string} The lead email
   */
  get leadEmail() {
    return this.data.leadEmail || '';
  }

  /**
   * Get the text of the last message in this conversation
   * @returns {string} The last message text
   */
  get lastMessageText() {
    return this.data.lastMessageText || '';
  }

  /**
   * Get the timestamp of the last message in this conversation
   * @returns {Date|null} The last message date
   */
  get lastMessageAt() {
    return this.data.lastMessageAt?.toDate() || null;
  }

  /**
   * Whether the last message was an internal comment (vs SMS/email).
   * @returns {boolean}
   */
  get lastMessageIsComment() {
    return !!this.data.lastMessageIsComment;
  }

  /**
   * Display name of the IS agent who last sent an outbound message to this lead.
   * @returns {string|undefined}
   */
  get lastOutgoingIsAgentName() {
    return this.data.lastOutgoingIsAgentName;
  }

  /**
   * Whether the AI SDR is currently handling this conversation. Mirrored from the lead
   * document so the inbox list can label bot-handled rows without a per-row lead read.
   * Absent on documents written before the mirror shipped, which reads as `false`.
   * @returns {boolean}
   */
  get useAutomatedSdr() {
    return !!this.data.useAutomatedSdr;
  }

  /**
   * Id of the user who claimed this conversation.
   * @returns {string|undefined}
   */
  get claimedBy() {
    return this.data.claimedBy;
  }

  /**
   * Display name of the user who claimed this conversation.
   * @returns {string|undefined}
   */
  get claimedByUsername() {
    return this.data.claimedByUsername;
  }

  /**
   * Timestamp when this conversation was claimed.
   * @returns {Date|null}
   */
  get claimedAt() {
    return this.data.claimedAt?.toDate() || null;
  }

  /**
   * Whether the current claim is still valid (within 1 hour).
   * @returns {boolean}
   */
  get isClaimValid() {
    return !!this.claimedAt
      && moment().diff(moment(this.claimedAt), 'hours') < LEAD_CLAIM_VALIDITY_HOURS;
  }

  get igIsArchived() {
    return !!this.data.igIsArchived;
  }

  get lastIgMessageAt() {
    return this.data.lastIgMessageAt?.toDate() || null;
  }

  get lastIgMessageText() {
    return this.data.lastIgMessageText || '';
  }

  /**
   * Archive this conversation by setting isArchived to true.
   * @param {string} [archivedBy] - The ID of the user performing the archive.
   * @returns {Promise<void>}
   */
  async archive(archivedBy) {
    return this.updateFields({
      isArchived: true,
      archivedAt: new Date(),
      archivedBy: archivedBy || null,
      unarchivedBy: null,
      unarchivedAt: null,
    });
  }

  /**
   * Unarchive this conversation by setting isArchived to false.
   * @param {string} [unarchivedBy] - The ID of the user performing the unarchive.
   * @returns {Promise<void>}
   */
  async unarchive(unarchivedBy) {
    return this.updateFields({
      isArchived: false,
      archivedAt: null,
      archivedBy: null,
      unarchivedBy: unarchivedBy || null,
      unarchivedAt: new Date(),
    });
  }

  async archiveIG(archivedBy) {
    return this.updateFields({
      igIsArchived: true,
      igArchivedAt: new Date(),
      igArchivedBy: archivedBy || null,
    });
  }

  async unarchiveIG() {
    return this.updateFields({
      igIsArchived: false,
      igArchivedAt: null,
      igArchivedBy: null,
    });
  }

  /**
   * Fetch a lead conversation by its document ID
   * @param {string} id - The document ID of the conversation
   * @returns {Promise<LeadConversation|null>} The conversation document, or null if not found
   */
  static async getById(id) {
    const doc = new LeadConversation(id);
    await doc.init();
    return doc.exists ? doc : null;
  }

  /**
   * Claim this conversation for a user. Transaction-safe: only claims if currently
   * unclaimed or the existing claim has expired (>= 1 hour old).
   *
   * @param {string} userId - UID of the user claiming.
   * @param {string} username - Display name of the user claiming.
   * @returns {Promise<Object>} Resolves to the resulting claim data
   *   (new claim if successful, existing claim data if already claimed by another user).
   */
  claim(userId, username) {
    const db = getFirebase().default.firestore();
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);
      if (!doc.exists) return null;

      const { claimedBy: currentClaimee, claimedAt } = doc.data();
      const newClaimData = {
        claimedBy: userId,
        claimedByUsername: username,
        claimedAt: moment().toDate(),
      };

      const isExpired = !!claimedAt
        && moment().diff(moment(claimedAt.toDate()), 'hours') >= LEAD_CLAIM_VALIDITY_HOURS;

      if (!currentClaimee || isExpired) {
        transaction.update(this.ref, newClaimData);
        return newClaimData;
      }

      // Already claimed by someone else with a valid claim — return their data
      return doc.data();
    });
  }

  /**
   * Remove the claim on this conversation. Only removes the claim if the
   * current claimee matches `userId` (prevents SDRs unclaiming each other).
   *
   * @param {string} userId - UID of the user unclaiming.
   * @returns {Promise<void>}
   */
  unclaim(userId) {
    const db = getFirebase().default.firestore();
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);
      if (!doc.exists) return;

      if (doc.data().claimedBy === userId) {
        transaction.update(this.ref, {
          claimedBy: getFirebase().firestore.FieldValue.delete(),
          claimedByUsername: getFirebase().firestore.FieldValue.delete(),
          claimedAt: getFirebase().firestore.FieldValue.delete(),
        });
      }
    });
  }

  /**
   * Force-claim this conversation, overwriting any existing claim.
   * Used when a user explicitly confirms they want to take over.
   *
   * @param {string} userId - UID of the user reclaiming.
   * @param {string} username - Display name of the user reclaiming.
   * @returns {Promise<void>}
   */
  reclaim(userId, username) {
    const db = getFirebase().default.firestore();
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);
      if (!doc.exists) return;

      transaction.update(this.ref, {
        claimedBy: userId,
        claimedByUsername: username,
        claimedAt: moment().toDate(),
      });
    });
  }

  /**
   * Get a collection of conversations filtered by archived status, ordered by most recent message.
   * Supports pagination via the optional startAfterDoc parameter.
   * @param {boolean} isArchived - If true, returns archived conversations; if false, returns open (non-archived)
   * @param {Object} [options] - Optional parameters
   * @param {BaseDocument} [options.startAfterDoc] - The last document from the previous page for pagination
   * @param {number} [options.limit=CONVERSATIONS_PAGE_SIZE] - Maximum number of conversations to retrieve
   * @returns {Collection} A Firestorter Collection of conversations
   */
  static getConversations(isArchived, { startAfterDoc, limit = CONVERSATIONS_PAGE_SIZE } = {}) {
    return new Collection(Collections.LEAD_CONVERSATION, {
      createDocument: (src, opts) => new LeadConversation(src.id, opts),
      query: (ref) => {
        let query = ref
          .where('isArchived', '==', isArchived)
          .orderBy('lastMessageAt', 'desc');
        if (startAfterDoc) {
          query = query.startAfter(startAfterDoc.snapshot);
        }
        return query.limit(limit);
      },
    });
  }

  static getIGConversations(igIsArchived, { startAfterDoc, limit = CONVERSATIONS_PAGE_SIZE } = {}) {
    return new Collection(Collections.LEAD_CONVERSATION, {
      createDocument: (src, opts) => new LeadConversation(src.id, opts),
      query: (ref) => {
        let query = ref
          .where('igIsArchived', '==', igIsArchived)
          .orderBy('lastIgMessageAt', 'desc');
        if (startAfterDoc) {
          query = query.startAfter(startAfterDoc.snapshot);
        }
        return query.limit(limit);
      },
    });
  }
}

export default LeadConversation;
export {
  MessageDirection,
  CONVERSATIONS_PAGE_SIZE,
};
